// src/pages/SalesList.tsx
// ✅ 새로고침 없이 행만 부분 갱신(상태/출고일/발행일/분할결제 등록·삭제)
// ✅ 분할 결제 등록 응답에 id가 없을 때도 재조회로 id 동기화 → 즉시 삭제 가능

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, MenuItem, CircularProgress, Container, Button, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import koLocale from 'date-fns/locale/ko';

// ===== Types =====
interface PaymentSchedule {
  id?: number;           // 응답이 id를 안 줄 수도 있어 임시 ID 허용
  amount: number;
  due_date: string;      // yyyy-MM-dd
  note?: string;
}

interface SaleItem {
  id: number;
  item: string;
  amount: number;
  state: string | null;
  release_date: string | null;
  completion_date: string | null;
  sale_id: number;
  payments?: PaymentSchedule[];
}

interface Sale {
  id: number;
  contract_date: string;
  total_amount: number;
  estimate_file?: string;
  created_at: string;
  company: { id: number; name: string };
  site: { id: number; name: string };
  items: SaleItem[];
}

const stateOptions = [
  { value: '대기', label: '대기' },
  { value: '생산', label: '생산' },
  { value: '생산완료', label: '생산완료' },
  { value: '출고', label: '출고' },
  { value: '계산서발행', label: '계산서발행' },
  { value: '입고', label: '입고' },
];

const SalesList: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const [completionDateDialog, setCompletionDateDialog] = useState<{ open: boolean; item: SaleItem | null }>({ open: false, item: null });
  const [completionDate, setCompletionDate] = useState<Date | null>(null);
  const [splitDialog, setSplitDialog] = useState<{ open: boolean; itemId: number | null }>({ open: false, itemId: null });
  const [splitAmount, setSplitAmount] = useState(0);
  const [splitDueDate, setSplitDueDate] = useState('');
  const [splitNote, setSplitNote] = useState('');

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/sales');
      setSales(response.data);
    } catch (error) {
      enqueueSnackbar('매출 목록을 불러오는데 실패했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ===== Local patch helpers =====
  const patchSaleItem = (itemId: number, patch: Partial<SaleItem>) => {
    setSales(prev =>
      prev.map(sale => ({
        ...sale,
        items: sale.items.map(it => (it.id === itemId ? { ...it, ...patch } : it)),
      })),
    );
  };

  const overwritePayments = (itemId: number, payments: PaymentSchedule[]) => {
    setSales(prev =>
      prev.map(sale => ({
        ...sale,
        items: sale.items.map(it =>
          it.id === itemId ? { ...it, payments: [...payments] } : it
        ),
      }))
    );
  };

  const addPaymentToItem = (itemId: number, payment: PaymentSchedule) => {
    setSales(prev =>
      prev.map(sale => ({
        ...sale,
        items: sale.items.map(it => {
          if (it.id !== itemId) return it;
          const next = [...(it.payments || []), payment];
          next.sort((a, b) => (a.due_date > b.due_date ? 1 : a.due_date < b.due_date ? -1 : 0));
          return { ...it, payments: next };
        }),
      })),
    );
  };

  const removePaymentFromItem = (itemId: number, paymentId: number) => {
    setSales(prev =>
      prev.map(sale => ({
        ...sale,
        items: sale.items.map(it =>
          it.id === itemId
            ? { ...it, payments: (it.payments || []).filter(p => p.id !== paymentId) }
            : it,
        ),
      })),
    );
  };

  // ===== Handlers =====
  const handleOpenCompletionDialog = (item: SaleItem) => {
    setCompletionDateDialog({ open: true, item });
    setCompletionDate(null);
  };

  const handleCloseCompletionDialog = () => {
    setCompletionDateDialog({ open: false, item: null });
    setCompletionDate(null);
  };

  const handleDeleteCompletionDate = async (item: SaleItem) => {
    if (!item.completion_date) return;
    if (!window.confirm('계산서 발행일을 삭제하시겠습니까?')) return;
    try {
      await api.put(`/api/sale-items/${item.id}`, { completion_date: null });
      patchSaleItem(item.id, { completion_date: null });
      enqueueSnackbar('계산서 발행일이 삭제되었습니다.', { variant: 'success' });
    } catch {
      enqueueSnackbar('계산서 발행일 삭제 실패', { variant: 'error' });
    }
  };

  const handleDownloadEstimate = async (saleId: number) => {
    try {
      const { data } = await api.get(`/api/sales/${saleId}/files/estimate`);
      const link = document.createElement('a');
      link.href = data.url;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      enqueueSnackbar('견적서 다운로드 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };

  const handleEdit = async (item: SaleItem, changes: Partial<SaleItem>) => {
    try {
      const updateData: any = {};
      if ('state' in changes) updateData.state = changes.state;
      if ('release_date' in changes) updateData.release_date = changes.release_date;
      if ('completion_date' in changes) updateData.completion_date = changes.completion_date;

      await api.put(`/api/sale-items/${item.id}`, updateData);
      patchSaleItem(item.id, updateData);
      enqueueSnackbar('항목 정보가 수정되었습니다.', { variant: 'success' });
    } catch {
      enqueueSnackbar('항목 수정에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleStateChange = async (item: SaleItem, newState: string) => {
    if (newState === '계산서발행') {
      handleOpenCompletionDialog(item);
    } else {
      await handleEdit(item, { state: newState });
    }
  };

  const handleCompletionDateSubmit = async (date: Date | null) => {
    if (!completionDateDialog.item || !date) return;
    try {
      await handleEdit(completionDateDialog.item, {
        state: '계산서발행',
        completion_date: format(date, 'yyyy-MM-dd'),
      });
      setCompletionDateDialog({ open: false, item: null });
    } catch {
      /* no-op */
    }
  };

  const handleReleaseDateChange = async (item: SaleItem, date: Date | null) => {
    try {
      const payload = { release_date: date ? format(date, 'yyyy-MM-dd') : null };
      await api.put(`/api/sale-items/${item.id}`, payload);
      patchSaleItem(item.id, payload);
      enqueueSnackbar(date ? '출고일이 수정되었습니다.' : '출고일이 삭제되었습니다.', { variant: 'success' });
    } catch {
      enqueueSnackbar('출고일 수정/삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  // ✅ 분할 결제 등록: 응답에 id가 없으면 해당 아이템의 결제 목록만 재조회해 id 동기화
  const handleSplitPaymentSubmit = async () => {
    if (!splitDialog.itemId || !splitAmount || !splitDueDate) return;
    const itemId = splitDialog.itemId;

    try {
      // 1) 서버에 등록
      const { data } = await api.post(`/api/sale-items/${itemId}/payments`, {
        amount: splitAmount,
        due_date: splitDueDate,
        note: splitNote || undefined,
      });

      // 2) 응답에서 id 해석 시도 (여러 케이스 대응)
      const created =
        data?.payment ?? // { payment: {...} }
        (typeof data === 'object' ? data : null); // { id, ... } or 기타

      const createdId =
        created?.id ??
        data?.id ??
        data?.payment_id ??
        null;

      if (createdId) {
        // ✅ id가 온 경우: 바로 로컬에 추가
        addPaymentToItem(itemId, {
          id: createdId,
          amount: created?.amount ?? splitAmount,
          due_date: created?.due_date ?? splitDueDate,
          note: created?.note ?? (splitNote || undefined),
        });
      } else {
        // ⚠️ id가 없을 경우: 아이템의 결제 목록만 재조회해 동기화
        try {
          // 백엔드에 이 엔드포인트가 있다고 가정: GET /api/sale-items/{itemId}/payments
          const res = await api.get(`/api/sale-items/${itemId}/payments`);
          const payments: PaymentSchedule[] = res.data;
          overwritePayments(itemId, payments);
        } catch {
          // 최후의 수단: 임시 ID로라도 추가 (삭제는 재조회 후 가능)
          addPaymentToItem(itemId, {
            id: Number(`9${Date.now()}`),
            amount: splitAmount,
            due_date: splitDueDate,
            note: splitNote || undefined,
          });
        }
      }

      enqueueSnackbar('분할 결제가 등록되었습니다.', { variant: 'success' });
    } catch {
      enqueueSnackbar('분할 결제 등록 실패', { variant: 'error' });
    } finally {
      // 다이얼로그 리셋
      setSplitDialog({ open: false, itemId: null });
      setSplitAmount(0);
      setSplitDueDate('');
      setSplitNote('');
    }
  };

  // ✅ 삭제 클릭 시 id가 없으면 재조회로 id를 찾아 삭제 시도
  const handleDeletePayment = async (item: SaleItem, payment: PaymentSchedule) => {
    try {
      let targetId = payment.id;

      if (!targetId) {
        // 방금 추가되어 id를 모를 때: 목록 재조회 후 동일 레코드 추정
        const res = await api.get(`/api/sale-items/${item.id}/payments`);
        const fresh: PaymentSchedule[] = res.data;

        // amount + due_date + note로 후보 매칭 (동일 금액/날짜/메모인 마지막 항목)
        const match = [...fresh]
          .reverse()
          .find(p => p.amount === payment.amount && p.due_date === payment.due_date && (p.note || '') === (payment.note || ''));

        if (match?.id) {
          // 화면의 임시 항목을 서버 항목으로 치환
          overwritePayments(item.id, fresh);
          targetId = match.id;
        }
      }

      if (!targetId) {
        enqueueSnackbar('결제 ID를 찾지 못했습니다. 잠시 후 다시 시도하세요.', { variant: 'warning' });
        return;
      }

      await api.delete(`/api/sale-items/payments/${targetId}`);
      removePaymentFromItem(item.id, targetId);
      enqueueSnackbar('삭제 완료', { variant: 'success' });
    } catch {
      enqueueSnackbar('삭제 실패', { variant: 'error' });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>매출 목록</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>계약일</TableCell>
                    <TableCell>업체명</TableCell>
                    <TableCell>현장</TableCell>
                    <TableCell align="right">총 계약금액(천원)</TableCell>
                    <TableCell align="center">견적서</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales.map((sale) => (
                    <React.Fragment key={sale.id}>
                      <TableRow>
                        <TableCell>{format(new Date(sale.contract_date), 'yyyy-MM-dd')}</TableCell>
                        <TableCell>{sale.company.name}</TableCell>
                        <TableCell>{sale.site.name}</TableCell>
                        <TableCell align="right">{sale.total_amount.toLocaleString()}</TableCell>
                        <TableCell align="center">
                          {sale.estimate_file ? (
                            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownloadEstimate(sale.id)}>
                              다운로드
                            </Button>
                          ) : '-'}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={5}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableHead>
                              <TableRow>
                                {[
                                  <TableCell key="h1" sx={{ width: '20%' }}>항목</TableCell>,
                                  <TableCell key="h2" align="right" sx={{ width: '20%' }}>금액(천원)</TableCell>,
                                  <TableCell key="h3" align="center" sx={{ width: '15%' }}>상태</TableCell>,
                                  <TableCell key="h4" align="center" sx={{ width: '15%' }}>출고일</TableCell>,
                                  <TableCell key="h5" align="center" sx={{ width: '10%' }}>금액 분할</TableCell>,
                                  <TableCell key="h6" align="center" sx={{ width: '15%' }}>계산서 발행일</TableCell>,
                                ]}
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {sale.items.map((item) => (
                                <React.Fragment key={item.id}>
                                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ verticalAlign: 'middle', width: '30%' }}>{item.item}</TableCell>

                                    <TableCell align="right" sx={{ verticalAlign: 'middle', width: '20%' }}>
                                      {item.amount.toLocaleString()}
                                    </TableCell>

                                    <TableCell align="center" sx={{ width: '15%' }}>
                                      <TextField
                                        select
                                        value={item.state || ''}
                                        onChange={(e) => handleStateChange(item, e.target.value)}
                                        size="small"
                                        sx={{ width: '100%' }}
                                      >
                                        {stateOptions.map((opt) => (
                                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                      </TextField>
                                    </TableCell>

                                    {/* 출고일: DatePicker + Clear */}
                                    <TableCell align="center" sx={{ width: '20%' }}>
                                      <DatePicker
                                        value={item.release_date ? new Date(item.release_date) : null}
                                        onChange={(newValue) => handleReleaseDateChange(item, newValue)}
                                        format="yy-MM-dd"
                                        slotProps={{
                                          textField: { fullWidth: true, size: 'small', sx: { width: '100%' } },
                                          actionBar: { actions: ['clear'] },
                                        }}
                                      />
                                    </TableCell>

                                    <TableCell align="center" sx={{ width: '10%' }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ minWidth: 90 }}
                                        onClick={() => setSplitDialog({ open: true, itemId: item.id })}
                                      >
                                        분할 결제
                                      </Button>
                                    </TableCell>

                                    {/* 계산서 발행일: 텍스트 + 삭제 버튼 */}
                                    <TableCell align="center" sx={{ width: '15%' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                        <Box sx={{ minWidth: 96, textAlign: 'center', fontSize: '0.85rem' }}>
                                          {item.completion_date ? format(new Date(item.completion_date), 'yyyy-MM-dd') : '-'}
                                        </Box>
                                        <Button
                                          size="small"
                                          color="error"
                                          variant="outlined"
                                          disabled={!item.completion_date}
                                          onClick={() => handleDeleteCompletionDate(item)}
                                        >
                                          삭제
                                        </Button>
                                      </Box>
                                    </TableCell>
                                  </TableRow>

                                  {/* 하위 분할 결제 내역 */}
                                  {item.payments && item.payments.length > 0 && item.payments.map((p, idx) => (
                                    <TableRow key={`payment-${item.id}-${p.id ?? `temp-${idx}`}`}>
                                      <TableCell sx={{ pl: 4, fontStyle: 'italic', color: 'gray' }}>
                                        └ 분할 {idx + 1}
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: 'gray' }}>{p.amount.toLocaleString()}</TableCell>
                                      <TableCell align="center" sx={{ color: 'gray' }}>{p.note || '-'}</TableCell>
                                      <TableCell align="left" sx={{ color: 'gray' }}>{format(new Date(p.due_date), 'yyyy-MM-dd')}</TableCell>
                                      <TableCell align="center">
                                        <Button
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeletePayment(item, p)}
                                        >
                                          삭제
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* 거래 완료 날짜 다이얼로그 */}
      <Dialog open={completionDateDialog.open} onClose={() => setCompletionDateDialog({ open: false, item: null })}>
        <DialogTitle>세금계산서 날짜 설정</DialogTitle>
        <DialogContent>
          <DatePicker
            label="완료 날짜"
            value={completionDate}
            onChange={(date) => setCompletionDate(date)}
            slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseCompletionDialog()}>취소</Button>
          <Button
            variant="contained"
            onClick={() => handleCompletionDateSubmit(completionDate)}
            disabled={!completionDate}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>

      {/* 분할 결제 다이얼로그 */}
      <Dialog open={splitDialog.open} onClose={() => setSplitDialog({ open: false, itemId: null })}>
        <DialogTitle>분할 결제 등록</DialogTitle>
        <DialogContent>
          <TextField
            label="금액"
            type="number"
            fullWidth
            value={splitAmount}
            onChange={(e) => setSplitAmount(parseFloat(e.target.value))}
            margin="normal"
          />
          <TextField
            label="예정일"
            type="date"
            fullWidth
            value={splitDueDate}
            onChange={(e) => setSplitDueDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="메모"
            fullWidth
            value={splitNote}
            onChange={(e) => setSplitNote(e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitDialog({ open: false, itemId: null })}>취소</Button>
          <Button onClick={handleSplitPaymentSubmit} variant="contained">등록</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SalesList;
